import React, { useEffect, useMemo, useState } from "react";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import { toast } from "react-toastify";
import PageHeader from "components/layout/PageHeader";
import api from "utils/apiService";

const emptyForm = {
  arabic: "",
  english: "",
  groupKey: "",
  groupArabic: "",
  groupEnglish: "",
  sortOrder: 0,
  active: true,
};

const labels = {
  contractor: "مهارات المقاولين",
  supplier: "تخصصات الموردين",
};

const SkillCategories = () => {
  const [kind, setKind] = useState("contractor");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedGroupKey, setSelectedGroupKey] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const filteredItems = useMemo(() => {
    return [...items]
      .filter((item) => item.kind === kind)
      .sort((a, b) => {
        const groupA = a.groupKey || "";
        const groupB = b.groupKey || "";
        if (groupA !== groupB) return groupA.localeCompare(groupB);
        return (a.sortOrder || 0) - (b.sortOrder || 0);
      });
  }, [items, kind]);

  const supplierGroups = useMemo(() => {
    const groups = new Map();
    const supplierItems = filteredItems.filter((item) => item.isGroup !== true);

    filteredItems
      .filter((item) => item.isGroup === true)
      .forEach((item) => {
        groups.set(item.key, {
          id: item.key,
          groupKey: item.key,
          arabic: item.arabic || "بدون مجموعة",
          english: item.english || "No group",
          sortOrder: item.sortOrder || 0,
          active: item.active !== false,
          totalCount: 0,
          activeCount: 0,
          items: [],
        });
      });

    supplierItems.forEach((item) => {
      const id = item.groupKey || "__ungrouped";
      const existing = groups.get(id) || {
        id,
        groupKey: item.groupKey || "",
        arabic: item.groupArabic || "بدون مجموعة",
        english: item.groupEnglish || "No group",
        sortOrder: item.sortOrder || 0,
        active: true,
        totalCount: 0,
        activeCount: 0,
        items: [],
      };

      existing.sortOrder = Math.min(existing.sortOrder, item.sortOrder || 0);
      existing.totalCount += 1;
      if (item.active !== false) existing.activeCount += 1;
      existing.items.push(item);
      groups.set(id, existing);
    });

    return [...groups.values()].sort((a, b) => {
      if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
      return a.arabic.localeCompare(b.arabic);
    });
  }, [filteredItems]);

  const selectedGroup = useMemo(
    () => supplierGroups.find((group) => group.id === selectedGroupKey) || null,
    [supplierGroups, selectedGroupKey]
  );

  const visibleItems =
    kind === "supplier" && selectedGroup
      ? selectedGroup.items
      : filteredItems.filter((item) => item.isGroup !== true);

  const load = async () => {
    setLoading(true);
    const res = await api.get("/skillCategories/admin?includeInactive=true");
    if (res.error) toast.error(res.error);
    else setItems(res.data || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setEditing(null);
    setForm({
      ...emptyForm,
      groupKey: selectedGroup?.groupKey || "",
      groupArabic:
        selectedGroup?.arabic === "بدون مجموعة"
          ? ""
          : selectedGroup?.arabic || "",
      groupEnglish:
        selectedGroup?.english === "No group"
          ? ""
          : selectedGroup?.english || "",
    });
  };

  const startEdit = (item) => {
    setEditing(item);
    setForm({
      arabic: item.arabic || "",
      english: item.english || "",
      groupKey: item.groupKey || "",
      groupArabic: item.groupArabic || "",
      groupEnglish: item.groupEnglish || "",
      sortOrder: item.sortOrder || 0,
      active: item.active !== false,
    });
  };

  const startEditGroup = (group) => {
    setSelectedGroupKey(null);
    setEditing({
      isGroup: true,
      groupKey: group.groupKey,
    });
    setForm({
      ...emptyForm,
      arabic: group.arabic === "بدون مجموعة" ? "" : group.arabic,
      english: group.english === "No group" ? "" : group.english,
      sortOrder: group.sortOrder || 0,
      active: group.active !== false,
    });
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    const selectedFormGroup = supplierGroups.find(
      (group) => group.groupKey === form.groupKey
    );
    const isSupplierGroupForm = kind === "supplier" && !selectedGroup;
    const body = isSupplierGroupForm
      ? {
          kind: "supplier",
          isGroup: true,
          arabic: form.arabic,
          english: form.english,
          sortOrder: Number(form.sortOrder || 0),
          active: form.active,
        }
      : {
          ...form,
          kind,
          sortOrder: Number(form.sortOrder || 0),
          groupKey: form.groupKey || null,
          groupArabic: selectedFormGroup?.arabic || form.groupArabic || null,
          groupEnglish: selectedFormGroup?.english || form.groupEnglish || null,
        };
    const res = editing
      ? editing.isGroup
        ? await api.patch(
            `/skillCategories/supplier-groups/${editing.groupKey}`,
            body
          )
        : await api.patch(`/skillCategories/${editing.id || editing._id}`, body)
      : await api.post("/skillCategories", body);
    setSaving(false);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    toast.success(editing ? "تم تحديث التصنيف" : "تم إضافة التصنيف");
    resetForm();
    load();
  };

  const toggleActive = async (item) => {
    const res = await api.patch(`/skillCategories/${item.id || item._id}`, {
      active: item.active === false,
    });
    if (res.error) toast.error(res.error);
    else {
      toast.success("تم تحديث حالة التصنيف");
      load();
    }
  };

  const seedDefaults = async () => {
    const res = await api.post("/skillCategories/seed/defaults");
    if (res.error) toast.error(res.error);
    else {
      toast.success("تمت مزامنة التصنيفات الافتراضية");
      load();
    }
  };

  const openGroup = (group) => {
    setSelectedGroupKey(group.id);
    setEditing(null);
    setForm({
      ...emptyForm,
      groupKey: group.groupKey || "",
      groupArabic: group.arabic === "بدون مجموعة" ? "" : group.arabic,
      groupEnglish: group.english === "No group" ? "" : group.english,
    });
  };

  const closeGroup = () => {
    setSelectedGroupKey(null);
    setEditing(null);
    setForm(emptyForm);
  };

  return (
    <div className="mt-6">
      <PageHeader
        title="إدارة المهارات والتخصصات"
        subtitle="تحكم في الخيارات التي تظهر للمقاولين والموردين داخل التطبيق"
        filters={
          <button
            type="button"
            onClick={seedDefaults}
            className="rounded-lg border border-info-600 px-4 py-2 text-sm font-semibold text-info-700 transition hover:bg-info-50"
          >
            مزامنة الافتراضي
          </button>
        }
      />

      <div className="mb-4 flex gap-2">
        {Object.entries(labels).map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => {
              setKind(value);
              setSelectedGroupKey(null);
              setEditing(null);
              setForm(emptyForm);
            }}
            className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
              kind === value
                ? "bg-info-600 text-white"
                : "border border-border bg-white text-navy-700"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <form
        onSubmit={submit}
        className="mb-5 grid gap-3 rounded-lg border border-border-muted bg-white p-5 shadow-card dark:border-navy-700 dark:bg-navy-800 md:grid-cols-3"
      >
        <Field
          label="الاسم العربي"
          value={form.arabic}
          onChange={(arabic) => setForm((prev) => ({ ...prev, arabic }))}
        />
        <Field
          label="الاسم الإنجليزي"
          value={form.english}
          onChange={(english) => setForm((prev) => ({ ...prev, english }))}
        />
        {kind === "supplier" && selectedGroup && (
          <SelectField
            label="التصنيف"
            value={form.groupKey}
            onChange={(groupKey) => {
              const group = supplierGroups.find(
                (item) => item.groupKey === groupKey
              );
              setForm((prev) => ({
                ...prev,
                groupKey,
                groupArabic: group?.arabic || "",
                groupEnglish: group?.english || "",
              }));
            }}
            options={supplierGroups
              .filter((group) => group.groupKey)
              .map((group) => ({
                value: group.groupKey,
                label: group.arabic,
              }))}
          />
        )}
        <Field
          label="الترتيب"
          type="number"
          value={form.sortOrder}
          onChange={(sortOrder) => setForm((prev) => ({ ...prev, sortOrder }))}
        />
        <label className="flex items-center gap-2 pt-6 text-sm font-medium text-gray-700 dark:text-gray-200">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(event) =>
              setForm((prev) => ({ ...prev, active: event.target.checked }))
            }
          />
          فعال
        </label>
        <div className="flex items-end gap-2">
          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-info-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-info-700 disabled:opacity-60"
          >
            {saving ? "جاري الحفظ" : editing ? "تحديث" : "إضافة"}
          </button>
          {editing && (
            <button
              type="button"
              onClick={resetForm}
              className="rounded-lg border border-border px-5 py-2 text-sm font-semibold text-navy-700"
            >
              إلغاء
            </button>
          )}
        </div>
      </form>

      <div className="rounded-lg border border-border-muted bg-white p-5 shadow-card dark:border-navy-700 dark:bg-navy-800">
        {loading ? (
          <div className="py-12 text-center text-sm text-gray-500">
            جاري التحميل
          </div>
        ) : kind === "supplier" && !selectedGroup ? (
          supplierGroups.length > 0 ? (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {supplierGroups.map((group) => (
                <div
                  key={group.id}
                  className="dark:hover:border-info-400 flex min-h-[112px] items-center justify-between rounded-lg border border-border bg-surface-muted p-4 text-start transition hover:border-info-500 hover:bg-white dark:border-navy-700 dark:bg-navy-900"
                >
                  <button
                    type="button"
                    onClick={() => openGroup(group)}
                    className="min-w-0 flex-1 text-start"
                  >
                    <span className="block text-base font-semibold text-navy-700 dark:text-white">
                      {group.arabic}
                    </span>
                    <span className="mt-1 block text-sm text-gray-500">
                      {group.english}
                    </span>
                    <span className="mt-3 block text-xs font-semibold text-gray-500">
                      {group.activeCount} فعال من {group.totalCount}
                    </span>
                  </button>
                  <div className="flex shrink-0 items-center gap-2">
                    {group.groupKey && (
                      <button
                        type="button"
                        onClick={() => startEditGroup(group)}
                        className="rounded-lg border border-info-600 px-3 py-1 text-xs font-semibold text-info-700"
                      >
                        تعديل
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => openGroup(group)}
                      className="rounded-full p-2 text-info-600 transition hover:bg-info-50 dark:hover:bg-info-500/10"
                      aria-label="فتح التصنيف"
                    >
                      <MdChevronLeft className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-10 text-center text-gray-500">
              لا توجد تصنيفات
            </div>
          )
        ) : (
          <div>
            {kind === "supplier" && selectedGroup && (
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-navy-700 dark:text-white">
                    {selectedGroup.arabic}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {selectedGroup.english}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeGroup}
                  className="inline-flex items-center gap-1 rounded-lg border border-border px-4 py-2 text-sm font-semibold text-navy-700 transition hover:border-info-500 hover:text-info-700 dark:border-navy-600 dark:text-white"
                >
                  <MdChevronRight className="h-5 w-5" />
                  رجوع للتصنيفات
                </button>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-start text-gray-500">
                    <th className="py-3 text-start">المفتاح</th>
                    <th className="py-3 text-start">العربي</th>
                    <th className="py-3 text-start">الإنجليزي</th>
                    {kind === "supplier" && (
                      <th className="py-3 text-start">المجموعة</th>
                    )}
                    <th className="py-3 text-start">الترتيب</th>
                    <th className="py-3 text-start">الحالة</th>
                    <th className="py-3 text-start">إجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleItems.map((item) => (
                    <tr
                      key={item.id || item._id}
                      className="border-b border-gray-100 last:border-b-0"
                    >
                      <td className="py-3 font-mono text-xs">{item.key}</td>
                      <td className="py-3 font-semibold">{item.arabic}</td>
                      <td className="py-3">{item.english}</td>
                      {kind === "supplier" && (
                        <td className="py-3">{item.groupArabic || "بدون"}</td>
                      )}
                      <td className="py-3">{item.sortOrder || 0}</td>
                      <td className="py-3">
                        <span
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${
                            item.active
                              ? "bg-green-50 text-green-700"
                              : "bg-gray-100 text-gray-500"
                          }`}
                        >
                          {item.active ? "فعال" : "غير فعال"}
                        </span>
                      </td>
                      <td className="py-3">
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => startEdit(item)}
                            className="rounded-lg border border-info-600 px-3 py-1 text-xs font-semibold text-info-700"
                          >
                            تعديل
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleActive(item)}
                            className={`rounded-lg border px-3 py-1 text-xs font-semibold transition ${
                              item.active
                                ? "border-red-400 text-red-500 hover:bg-red-50 dark:border-red-300 dark:text-red-200 dark:hover:bg-red-500/10"
                                : "border-green-500 text-green-600 hover:bg-green-50 dark:border-green-300 dark:text-green-200 dark:hover:bg-green-500/10"
                            }`}
                          >
                            {item.active ? "تعطيل" : "تفعيل"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {visibleItems.length === 0 && (
                    <tr>
                      <td colSpan={kind === "supplier" ? 7 : 6}>
                        <div className="py-10 text-center text-gray-500">
                          لا توجد تصنيفات
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({
  label,
  value,
  onChange,
  type = "text",
  disabled = false,
  placeholder,
  required = true,
}) => (
  <label className="flex flex-col gap-1">
    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
      {label}
    </span>
    <input
      type={type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
      placeholder={placeholder}
      required={required}
      className="rounded-lg border border-border bg-surface-muted px-3 py-2 text-sm text-navy-700 outline-none transition focus:border-info-500 focus:bg-white disabled:opacity-60 dark:border-navy-600 dark:bg-navy-900 dark:text-white"
    />
  </label>
);

const SelectField = ({ label, value, onChange, options, required = true }) => (
  <label className="flex flex-col gap-1">
    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
      {label}
    </span>
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      required={required}
      className="rounded-lg border border-border bg-surface-muted px-3 py-2 text-sm text-navy-700 outline-none transition focus:border-info-500 focus:bg-white dark:border-navy-600 dark:bg-navy-900 dark:text-white"
    >
      <option value="">اختر التصنيف</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </label>
);

export default SkillCategories;
